import { useEffect, useState } from "react";
import { applyProofEdit, cleanTexts, editsCatalog, menuDishes, textLabel, textSpots } from "@speisekarte/core";
import type { CardText } from "@speisekarte/core";
import { GRIP_PATH, useSortable } from "../sortable.ts";
import { useDraft, useKept } from "../kept.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import { SymbolInput, SymbolTextarea } from "./Symbols.tsx";
import { ProofBox, openEdits } from "./Proof.tsx";
import type { OpenEdit } from "./Proof.tsx";
import { PhraseLine } from "./TextWork.tsx";

interface Row {
  /** Stable key while editing. */
  key: number;
  /** The id on the server; undefined for a new one. */
  id?: string;
  title: string;
  body: string;
}

const rowsOf = (list: CardText[]): Row[] => list.map((t, key) => ({ key, id: t.id, title: t.title, body: t.body }));
let nextKey = 10_000;

/**
 * „Texte“ (S14): lines on a menu that are no dish, kept like the dishes and live on every menu, saved
 * as a whole like the categories, each change as one leaves the field (no „Speichern“, Martin,
 * 01.11.2026). One that a menu shows cannot go (the number says on how many).
 */
export function Texts({ focus = "" }: { focus?: string }) {
  const s = useStore();
  const data = s.data!;
  const canEdit = editsCatalog(s.role);

  // From the pencil at a text of a menu („#/textbausteine/<id>“): that text in view, its title to type in.
  useEffect(() => {
    if (!focus) return;
    const row = document.querySelector<HTMLElement>(`[data-testid="text-row"][data-id="${CSS.escape(focus)}"]`);
    row?.scrollIntoView({ block: "center" });
    row?.querySelector<HTMLInputElement>("input")?.focus();
  }, [focus]);
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  const saved = JSON.stringify(data.texts);
  // The list as being changed, kept while one looks elsewhere (S17); anew when it was saved.
  const [rows, setRows] = useDraft<Row[]>("texts", saved, () => rowsOf(data.texts));
  // New ones apart until they have a title or a text: saved, they join the list at its end.
  const [fresh, setFresh] = useKept<Row[]>("texts-new", []);
  const filled = (r: Row) => r.title.trim() !== "" || r.body.trim() !== "";
  const stored = new Map(data.texts.map((t) => [t.id, t]));
  const used = new Map<string, number>();
  for (const m of data.menus) for (const id of new Set(menuDishes(m))) used.set(id, (used.get(id) ?? 0) + 1);
  const sort = useSortable(
    (from, to) => {
      const next = [...rows];
      const [row] = next.splice(from.index, 1);
      next.splice(to.index, 0, row!);
      void save(next);
    },
    (from, by) => {
      const at = from.index + by;
      if (at < 0 || at >= rows.length) return;
      const next = [...rows];
      [next[from.index], next[at]] = [next[at]!, next[from.index]!];
      void save(next);
    },
  );
  // „Deutsch prüfen“ (S47): the corrections open in the texts as typed, taken into the row.
  const proofEdits = openEdits(
    data,
    rows.flatMap((r) => (r.id ? textSpots({ id: r.id, title: r.title, body: r.body }).map((s) => ({ ...s, where: `${textLabel(r)}, ${s.where}` })) : [])),
  );
  const take = (e: OpenEdit) => void save(rows.map((r) => (r.id && e.key === `text:${r.id}:title` ? { ...r, title: applyProofEdit(r.title, e.edit) } : r.id && e.key === `text:${r.id}:body` ? { ...r, body: applyProofEdit(r.body, e.edit) } : r)));
  const set = (key: number, change: Partial<Row>) => [setRows(rows.map((x) => (x.key === key ? { ...x, ...change } : x))), setState({})];
  const setNew = (key: number, change: Partial<Row>) => [setFresh(fresh.map((x) => (x.key === key ? { ...x, ...change } : x))), setState({})];

  /** The list with the new ones that have a title or a text; nothing when it is as saved. */
  async function save(next: Row[], pending = fresh) {
    const all = [...next, ...pending.filter(filled)];
    setRows(next);
    if (JSON.stringify(all.map((r) => [r.id, r.title, r.body])) === JSON.stringify(data.texts.map((t) => [t.id, t.title, t.body]))) return;
    const entries = all.map((r) => ({ ...(r.id ? { id: r.id } : {}), title: r.title, body: r.body }));
    const clean = cleanTexts(entries);
    if ("error" in clean) return setState({ error: clean.error });
    try {
      await store.change(() => api("/api/texts", { method: "PUT", body: { texts: entries } }));
      setFresh(pending.filter((r) => !filled(r)));
      setState({ ok: "Gespeichert" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  return (
    <div>
      <p>
        <a className="back" href="#/kataloge">← Kataloge</a>
      </p>
      <section className="card">
        <h2>Textbausteine</h2>
        <p className="hint">Zeilen auf einer Speisekarte, die keine Speise sind: „Tapa del día – Fragen Sie nach unseren Tagestapas!“ oder „Letzte Bestellung für die Küche 21 Uhr“. Ohne Nummer, Preis und Allergene; eine Änderung gilt auf jeder Speisekarte. Auf die Speisekarte kommen sie im Editor unter „Textbausteine“.</p>
        <ProofBox edits={proofEdits} take={take} canEdit={canEdit} />
        <ul className="list text-list" data-sort-root>
          <li data-sort-group="list" style={{ border: "none" }} className={sort.groupClass("list", rows.length)}>
            {rows.map((r, i) => {
              const n = r.id ? (used.get(r.id) ?? 0) : 0;
              return (
                <div key={r.key} className={`text-row ${r.id && r.id === focus ? "focus" : ""} ${sort.rowClass({ group: "list", index: i })}`} data-sort-row data-testid="text-row" data-id={r.id}>
                  <div className="text-fields">
                    <SymbolInput value={r.title} onValue={(v) => set(r.key, { title: v })} onBlur={() => void save(rows)} readOnly={!canEdit} aria-label="Überschrift" placeholder="Überschrift, z. B. Tapa del día" data-testid="text-title" />
                    <SymbolTextarea value={r.body} onValue={(v) => set(r.key, { body: v })} onBlur={() => void save(rows)} readOnly={!canEdit} aria-label="Text" placeholder="Text, z. B. Fragen Sie nach unseren Tagestapas!" rows={2} data-testid="text-body" />
                    {/* Their translations right here (S61 § 4.1), once saved as they stand. */}
                    {r.id && stored.get(r.id)?.title === r.title && <PhraseLine text={r.title} data={data} />}
                    {r.id && stored.get(r.id)?.body === r.body && <PhraseLine text={r.body} data={data} long />}
                  </div>
                  <span className="muted small count" title="Auf so vielen Speisekarten">
                    {n || ""}
                  </span>
                  {canEdit && (
                    <>
                      <button className="link danger" disabled={n > 0} onClick={() => void save(rows.filter((x) => x.key !== r.key))} aria-label="Textbaustein entfernen" title={n > 0 ? "Steht noch auf Speisekarten" : "Entfernen"}>
                        ✕
                      </button>
                      <span {...sort.handle({ group: "list", index: i }, r.title || "Textbaustein")}>
                        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                          <path d={GRIP_PATH} stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                        </svg>
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </li>
          {fresh.length > 0 && (
            <li style={{ border: "none" }}>
              {fresh.map((r) => (
                // Saved as the row is left, not between its two fields: it would move away under the fingers.
                <div key={r.key} className="text-row unsaved" data-testid="text-new" onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && void save(rows)}>
                  <div className="text-fields">
                    <SymbolInput value={r.title} onValue={(v) => setNew(r.key, { title: v })} aria-label="Überschrift" placeholder="Überschrift, z. B. Tapa del día" data-testid="text-title" autoFocus />
                    <SymbolTextarea value={r.body} onValue={(v) => setNew(r.key, { body: v })} aria-label="Text" placeholder="Text, z. B. Fragen Sie nach unseren Tagestapas!" rows={2} data-testid="text-body" />
                  </div>
                  <span className="count" />
                  <button className="link danger" onClick={() => setFresh(fresh.filter((x) => x.key !== r.key))} aria-label="Neuen Textbaustein entfernen" title="Entfernen">
                    ✕
                  </button>
                  {/* Where the grip will be once saved: the columns stay. */}
                  <span className="grip" style={{ visibility: "hidden" }} aria-hidden="true" />
                </div>
              ))}
            </li>
          )}
        </ul>
        {rows.length === 0 && fresh.length === 0 && <p className="muted">Noch keine Textbausteine.</p>}
        {fresh.length > 0 && (
          <p className="warn" data-testid="texts-unsaved">
            Noch nicht gespeichert: Ein neuer Textbaustein wird gespeichert, sobald er eine Überschrift oder einen Text hat und du die Zeile verlässt.
          </p>
        )}
        {canEdit && (
          <div className="buttons">
            <button onClick={() => [setFresh([...fresh, { key: nextKey++, title: "", body: "" }]), setState({})]} data-testid="add-text">
              + Textbaustein
            </button>
          </div>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        {state.ok && <p className="ok-text">{state.ok}</p>}
        <p className="hint">Jede Änderung wird gespeichert, sobald du das Feld verlässt.</p>
      </section>
    </div>
  );
}
